import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {
  AddSalesPersonRequest, BenchEntryDTO, BenchEntryPatch, BusinessUnitActivationDTO, businessUnitsByKey,
  BusinessUnitDTO, CreateBusinessUnitDTO, SalesPersonDTO, SelectableUserDTO, UpdateBusinessUnitDTO,
  UpdateSalesPersonRequest,
} from '../shared/model/bench-entry.model';

@Injectable({providedIn: 'root'})
export class BenchOverviewService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiConfig.uri + 'api/bench';
  private readonly buUrl = environment.apiConfig.uri + 'api/business-units';

  getEntries(): Observable<BenchEntryDTO[]> {
    return this.http.get<BenchEntryDTO[]>(`${this.baseUrl}/entries`);
  }

  getEntry(id: string): Observable<BenchEntryDTO> {
    return this.http.get<BenchEntryDTO>(`${this.baseUrl}/entries/${id}`);
  }

  createEntry(entry: BenchEntryDTO): Observable<BenchEntryDTO> {
    return this.http.post<BenchEntryDTO>(`${this.baseUrl}/entries`, entry);
  }

  getSales(): Observable<SalesPersonDTO[]> {
    return this.http.get<SalesPersonDTO[]>(`${this.baseUrl}/sales-persons`);
  }

  getSelectableUsers(): Observable<SelectableUserDTO[]> {
    return this.http.get<SelectableUserDTO[]>(`${this.baseUrl}/sales-persons/selectable-users`);
  }

  addSalesPerson(request: AddSalesPersonRequest): Observable<SalesPersonDTO> {
    return this.http.post<SalesPersonDTO>(`${this.baseUrl}/sales-persons`, request);
  }

  updateSalesPerson(id: string, request: UpdateSalesPersonRequest): Observable<SalesPersonDTO> {
    return this.http.put<SalesPersonDTO>(`${this.baseUrl}/sales-persons/${id}`, request);
  }

  deleteSalesPerson(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/sales-persons/${id}`);
  }

  updateEntry(id: string, entry: BenchEntryDTO): Observable<BenchEntryDTO> {
    return this.http.put<BenchEntryDTO>(`${this.baseUrl}/entries/${id}`, entry);
  }

  patchEntry(id: string, patch: BenchEntryPatch): Observable<BenchEntryDTO> {
    return this.http.patch<BenchEntryDTO>(`${this.baseUrl}/entries/${id}`, patch);
  }

  /**
   * Anhang eines Angebots (NP20-814). Der Upload ersetzt eine vorhandene Datei und
   * antwortet mit dem aktualisierten Eintrag. `note` ist massgeblich: fehlt sie,
   * loescht der Server eine gespeicherte Notiz.
   */
  uploadOfferAttachment(
    entryId: string, offerId: string, file: File, note: string | null,
  ): Observable<BenchEntryDTO> {
    const form = new FormData();
    form.append('file', file);
    if (note?.trim()) form.append('note', note);
    return this.http.post<BenchEntryDTO>(this.attachmentUrl(entryId, offerId), form);
  }

  /**
   * Als Blob, weil der Endpoint den JWT-Header braucht und eine reine Navigation
   * ihn nicht mitschickt. Die Antwort kommt vollstaendig zurueck — der Dateiname
   * steht im `Content-Disposition`-Header, nicht im Rumpf.
   */
  downloadOfferAttachment(entryId: string, offerId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(this.attachmentUrl(entryId, offerId), {
      observe: 'response',
      responseType: 'blob',
    });
  }

  deleteOfferAttachment(entryId: string, offerId: string): Observable<void> {
    return this.http.delete<void>(this.attachmentUrl(entryId, offerId));
  }

  private attachmentUrl(entryId: string, offerId: string): string {
    return `${this.baseUrl}/entries/${entryId}/offers/${offerId}/attachment`;
  }

  deleteEntry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/entries/${id}`);
  }

  /**
   * NP20-778: alphabetisch nach dem Kürzel, an der Quelle. Fünf Ansichten bieten die Business Units
   * zur Auswahl an (Suche in Sales-Ansicht, Übersicht und Meeting-Modus, die Auswahl am Angebot und
   * die BU-Übersicht) — keine hat sortiert. Lokal ergänzte Zeilen im Katalog-Dialog bleiben
   * unberührt, weil erst der nächste Ladevorgang neu sortiert.
   */
  getBusinessUnits(): Observable<BusinessUnitDTO[]> {
    return this.http.get<BusinessUnitDTO[]>(this.buUrl).pipe(map(businessUnitsByKey));
  }

  createBusinessUnit(request: CreateBusinessUnitDTO): Observable<BusinessUnitDTO> {
    return this.http.post<BusinessUnitDTO>(this.buUrl, request);
  }

  updateBusinessUnit(id: string, request: UpdateBusinessUnitDTO): Observable<BusinessUnitDTO> {
    return this.http.patch<BusinessUnitDTO>(`${this.buUrl}/${id}`, request);
  }

  deleteBusinessUnit(id: string): Observable<void> {
    return this.http.delete<void>(`${this.buUrl}/${id}`);
  }

  setBusinessUnitActivation(id: string, request: BusinessUnitActivationDTO): Observable<BusinessUnitDTO> {
    return this.http.put<BusinessUnitDTO>(`${this.buUrl}/${id}/activation`, request);
  }
}
